import React, { createContext, useReducer, useEffect, useCallback, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config'; // 假设您有一个 config.js 文件
import { useNavigate } from 'react-router-dom';

// 创建上下文
export const AuthContext = createContext();

// 初始状态
const initialState = {
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  user: null,
  error: null
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'USER_LOADED':
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload
      };
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('userInfo', JSON.stringify(action.payload.user));
      return {
        ...state,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case 'AUTH_ERROR':
    case 'LOGIN_FAIL':
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null,
        error: action.payload
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    // 添加一个新的action类型来明确设置loading状态为false
    case 'SET_LOADING_FALSE':
      return {
        ...state,
        loading: false
      };
    default:
      return state;
  }
};

// 提供者组件
export const AuthProvider = ({ children }) => {
  // 修改现有的代码，添加安全解析
  let initialUser = null;
  let initialToken = null;

  try {
    const userStr = localStorage.getItem('userInfo');
    const tokenStr = localStorage.getItem('token');
    
    if (userStr) initialUser = JSON.parse(userStr);
    if (tokenStr) initialToken = tokenStr;
  } catch (error) {
    console.error('解析本地存储数据出错，重置数据:', error);
    // 清理无效数据
    localStorage.removeItem('userInfo');
    localStorage.removeItem('token');
  }

  const [user, setUser] = useState(initialUser);
  const [token, setToken] = useState(initialToken);
  const [isAuthenticated, setIsAuthenticated] = useState(!!initialToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // 设置认证token
  const setAuthToken = (token) => {
    if (token) {
      // 确保使用正确的请求头名称
      axios.defaults.headers.common['x-auth-token'] = token;
      // 有些API可能使用这种格式
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['x-auth-token'];
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // 加载用户
  const loadUser = useCallback(async () => {
    // 从localStorage获取用户信息
    const userInfoStr = localStorage.getItem('userInfo');
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
    
    if (userInfo) {
      setUser(userInfo);
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }
    
    // 如果没有缓存的用户信息，尝试从API获取
    try {
      const res = await axios.get('/api/auth');
      setUser(res.data);
      setIsAuthenticated(true);
      setLoading(false);
    } catch (err) {
      console.error('加载用户失败:', err);
      setError(err.message || '加载用户失败');
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, []);

  // 登录用户
  const login = useCallback(async (formData) => {
    try {
      console.log('AuthContext login调用:', formData);
      
      // 支持两种登录方式：手机号或用户名
      const { username, phone, password } = formData;
      
      // 构造登录请求数据 - 简化为只发送需要的字段
      const loginData = {
        phone: phone || undefined,      // 优先使用手机号
        username: username || undefined, // 备用用户名
        password
      };
      
      console.log('准备发送登录请求到: /api/auth');
      console.log('登录数据:', loginData);
      
      // 调用登录API
      const res = await axios.post('/api/auth', loginData);
      console.log('登录响应:', res.data);
      
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('userInfo', JSON.stringify(res.data.user));
        
        setAuthToken(res.data.token);
        setUser(res.data.user);
        setIsAuthenticated(true);
        setLoading(false);
        
        // 修改这里：从主页面重定向到考试选择页面
        // 原代码可能是：
        // history.push('/');
        // 或者：
        // window.location.href = '/';
        
        // 新代码：
        navigate('/selection');
        
        return { success: true };
      } else {
        return { success: false, message: '登录失败，没有收到令牌' };
      }
    } catch (error) {
      console.error('登录失败详情:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      let errorMessage = '登录失败';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.msg || errorMessage;
        console.error('服务器返回错误:', error.response.data);
      }
      
      setError(errorMessage);
      setIsAuthenticated(false);
      setLoading(false);
      
      return { success: false, message: errorMessage };
    }
  }, []);

  // 登出
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    setAuthToken(null);
  }, []);

  // 清除错误
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 当token变化时加载用户
  useEffect(() => {
    // 设置一个超时，确保loading状态不会无限期挂起
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log('加载超时，强制设置loading为false');
        setLoading(false);
      }
    }, 5000); // 5秒超时
    
    if (token) {
      loadUser();
    } else {
      // 如果没有token，立即设置loading为false
      setLoading(false);
    }
    
    return () => clearTimeout(timeoutId);
  }, [token, loadUser, loading]);

  return (
    <AuthContext.Provider
      value={{
        token: token,
        isAuthenticated: isAuthenticated,
        loading: loading,
        user: user,
        error: error,
        login,
        logout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 